import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { ActivatedRoute, NavigationExtras, Params } from '@angular/router';
import {
    ChangeDetectorRef,
    Component,
    DoCheck,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
    computed,
    inject,
    signal,
} from '@angular/core';
import { Demand } from '../../types/Demand';
import { DemandShippingAddressChange, DemandShippingAddressOrigin } from '../../types/DemandShippingAddressChange';
import { UnableToRetreiveDemandError } from '../../types/error/UnableToRetreiveDemandError';
import {
    BottomSheetService,
    CustomRouter,
    NotificationMessageLevel,
    NotificationService,
    SentryUtilService,
    UploadBottomSheetResult,
    View,
} from '@lib/core';
import { DemandArticle } from '../../types/DemandArticle';
import { Article, ArticleAccountingChangeEvent, ArticlePrice } from '@lib/article';
import { DemandProduct } from '../../types/DemandProduct';
import { IDemandProductChangeResponse } from '../../types/IDemandProductChangeResponse';
import { IDemandProductPrice } from '../../types/DemandProductPrice';
import { DemandDataService } from '../../demand-data.service';
import { ISiteChange } from '@lib/customer-site';
import { firstValueFrom, Subject } from 'rxjs';
import { debounceTime, finalize, takeUntil, tap } from 'rxjs/operators';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { ShippingAddress } from '@lib/address';
import { candidateToShippingAddress, DeliveryAddressCandidatesDataService } from '@lib/delivery-address';
import { DialogOptions, DisplayCurrency, PurchaseRequisitionOrderStatus, SubscriptionHelper } from '@lib/util';
import { MatDialog } from '@angular/material/dialog';
import { DemandAddProductComponent, DemandAddProductDialogData } from './add-product/demand-add-product.component';
import {
    DemandEditProductComponent,
    DemandEditProductDialogData,
    RESULTS_IN,
} from './edit-product/demand-edit-product.component';
import { PurchaseRequisitionHistoryEntry } from '../../../purchase-requisition/types/PurchaseRequisitionHistoryEntry';
import { PurchaseRequisition } from '../../../purchase-requisition/types/PurchaseRequisition';
import { ClassificationCode, ItemClassificationDataService } from '@lib/classification';
import { IUser, UserService } from '@lib/user';
import {
    AccountingBaseDisplayType,
    AccountingChange,
    AccountingClassificationCandidate,
    AccountingSelectionV2Component,
    IAccountingBulkChangeSelection,
    ItemAccountingDataService,
} from '@lib/accounting';
import { DemandValidator } from './types/DemandValidator';
import {
    ISupplierEmailValidationResponse,
    SupplierEmailValidationMessage,
} from './types/ISupplierEmailValidationResponse';
import dayjs, { Dayjs } from 'dayjs';
import { PurchaseRequisitionStatusEnum } from '../../../purchase-requisition/types/PurchaseRequistionStatusEnum';
import { PurchaseRequisitionTask } from '../../../workflow/types/PurchaseRequisitionTask';
import { CommentDataService } from '@lib/comment';
import { FileDataService } from '@lib/file';
import { DemandSupplier } from '../../types/IDemandSupplierContact';
import { SupplierCurrencyCheckStatus, SupplierCurrencyStore } from './supplier-currency.store';
import { DemandPdfPreviewStore } from './demand-pdf-preview.store';
import { IDemandSupplierContactChange } from '../../util/demand-supplier-display/demand-supplier-display.component';
import { ClientDataService, ICustomerCompanySettings } from '@lib/client';
import { AclAction, AclResource, AclService } from '@lib/acl';
import { FormControl } from '@angular/forms';
import { PurchaseRequisitionResult } from '../../../purchase-requisition/types/PurchaseRequisitionResult';
import { I18nService } from '@lib/i18n';
import { BC, BreadcrumbService } from '@lib/shared/breadcrumbs';
import { TextModuleCategoryEnum } from '@lib/shared/text-module';
import { DemandAdditionalCostComponent } from './additional-costs/demand-additional-cost.component';
import {
    DemandUpdateDeliveryDateDialogComponent,
    DemandUpdateDeliveryDateDialogData,
    DemandUpdateDeliveryDateDialogResult,
} from './update-delivery-date-dialog/update-delivery-date-dialog.component';
import { CompanyCodeDataService, ICompanyCode } from '@lib/company-code';
import { PurchasingGroup, PurchasingGroupDataService } from '@lib/purchasing-group';
import { SupplierSearchService } from '@lib/supplier';
import { DemandSelectableCurrencies } from '../../types/IDemandSelectableCurrencies';
import { WorkflowFormKey } from '@lib/workflow-integration';
import { ProcessChainDataService, ProcessChainDocumentType, ProcessChainRelatedDocument } from '@lib/process-chain';

type ProcessChainLinkedDocumentView = ProcessChainRelatedDocument & {
    targetRoute: string[] | null;
};

const PURCHASING_GROUP_SELECT_PLACEHOLDER = marker('Einkaufsgruppe wählen');
const PURCHASING_GROUP_READONLY_PLACEHOLDER = marker('Keine Einkaufsgruppe ermittelt');
const PURCHASING_GROUP_LOADING_PLACEHOLDER = marker('Einkaufsgruppen werden geladen');
const PURCHASING_GROUP_EMPTY_PLACEHOLDER = marker('Keine aktive Einkaufsgruppe verfügbar');
const PURCHASING_GROUP_LOAD_FAILED_PLACEHOLDER = marker('Einkaufsgruppen konnten nicht geladen werden');
const DEMAND_DETAILS_LOAD_FAILED = marker('Teile der Bedarfsmeldung konnten nicht geladen werden.');

interface IPurchasingGroupOption {
    readonly code: string;
    readonly name: string;
    readonly label: string;
}

@Component({
    selector: 'demand-content',
    templateUrl: './demand-content.component.html',
    styleUrls: ['./demand-content.component.scss'],
    standalone: false,
    providers: [SupplierCurrencyStore, DemandPdfPreviewStore],
})
export class DemandContentComponent extends SubscriptionHelper implements OnInit, OnDestroy, OnChanges, DoCheck {
    private readonly router = inject(CustomRouter);
    private readonly route = inject(ActivatedRoute);
    private readonly dataService = inject(DemandDataService);
    private readonly commentDataService = inject(CommentDataService);
    private readonly itemClassificationDataService = inject(ItemClassificationDataService);
    private readonly fileDataService = inject(FileDataService);
    private readonly notificationService = inject(NotificationService);
    private readonly itemAccountingDataService = inject(ItemAccountingDataService);
    private readonly dialog = inject(MatDialog);
    private readonly bottomSheetService = inject(BottomSheetService);
    private readonly userService = inject(UserService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly companySettingService = inject(ClientDataService);
    private readonly i18n = inject(I18nService);
    private readonly aclService = inject(AclService);
    private readonly breadcrumbService = inject(BreadcrumbService);
    private readonly companyCodeDataService = inject(CompanyCodeDataService);
    private readonly purchasingGroupDataService = inject(PurchasingGroupDataService);
    private readonly supplierSearchService = inject(SupplierSearchService);
    private readonly deliveryAddressCandidatesDataService = inject(DeliveryAddressCandidatesDataService);
    private readonly processChainDataService = inject(ProcessChainDataService);
    private readonly sentry = inject(SentryUtilService);

    private readonly fetchedLinkedDocuments = signal<ProcessChainRelatedDocument[]>([]);
    readonly linkedProcessDocuments = computed<ProcessChainLinkedDocumentView[]>(() =>
        this.fetchedLinkedDocuments().map((document) => ({
            ...document,
            targetRoute: this.resolveLinkedDocumentRoute(document),
        })),
    );

    // Loading state of the requisition view. Each flag latches once its part has arrived, so a later
    // reload (e.g. the task/purchase requisition being re-assigned) never flips the page back to the skeleton.
    public readonly articlesLoaded = signal(false);
    private readonly historyLoaded = signal(false);
    private readonly linkedDocumentsLoading = signal(true);
    public readonly documentLayoutLoading = computed(
        () => !this.articlesLoaded() || !this.historyLoaded() || this.linkedDocumentsLoading(),
    );

    private purchasingGroups: readonly PurchasingGroup[] = [];
    // EPROC-11619: purchasingGroupOptionsFor() is a template method call, re-invoked for every position
    // on every change-detection cycle. purchasingGroups itself only changes once (on load), so cache
    // the derived, sorted option list by that array's identity instead of recomputing filter/map/sort
    // for every position on every CD cycle.
    private activePurchasingGroupOptionsCache?: {
        readonly source: readonly PurchasingGroup[];
        readonly options: readonly IPurchasingGroupOption[];
    };
    private purchasingGroupsLoaded = false;
    public loadingPurchasingGroups = false;
    public purchasingGroupsLoadFailed = false;

    @ViewChildren(AccountingSelectionV2Component)
    private readonly accountingSelectionComponents?: QueryList<AccountingSelectionV2Component>;
    @ViewChildren(DemandAdditionalCostComponent)
    private readonly additionalCostComponents?: QueryList<DemandAdditionalCostComponent>;

    // EPROC-11619: the accounting/site/material-group block per position is @defer'd (on viewport), but
    // checkInitStatus() below needs every position to eventually mount - a demand whose positions never
    // get scrolled into view must still reach "initialized" for the surrounding task container. The
    // previous fallback ("on timer(3s)") mounted all N positions in one burst once the timer fired,
    // which recreated exactly the request storm this ticket is about. Staggering the fallback in small
    // batches keeps the eventual-mount guarantee while bounding how many positions load their
    // accounting data at the same time. eagerLoadCutoffIndex starts at one batch so the first screen's
    // worth of rows never has to wait on the timer at all.
    private static readonly EAGER_LOAD_BATCH_SIZE = 5;
    private static readonly EAGER_LOAD_INTERVAL_MS = 400;
    public eagerLoadCutoffIndex = DemandContentComponent.EAGER_LOAD_BATCH_SIZE;
    private eagerLoadTimeoutHandle?: ReturnType<typeof setTimeout>;

    readonly MAX_TITLE_LENGTH = 150;

    @Input() demand!: Demand;
    @Input() purchaseRequisition?: PurchaseRequisition;
    @Input() editingWithDialog = false;
    @Input() history?: PurchaseRequisitionHistoryEntry[];
    @Input() supplierRequired = false;
    @Input() supplierChangeAllowed = true;
    @Input() validUntilRequired = false;
    @Output() validationStatusChange = new EventEmitter<boolean>();
    @Output() withdrawn = new EventEmitter<void>();
    @Output() suppliersChanged = new EventEmitter<DemandSupplier[]>();
    /**
     * Currency acceptance of the demand (EPROC-11425), re-emitted on every refresh so the embedding
     * approval form can gate the order conversion and show the reason next to its buttons.
     */
    @Output() currencyAcceptanceChanged = new EventEmitter<DemandSelectableCurrencies | undefined>();

    @Output() initialized = new EventEmitter<void>();

    /**
     * If Provided we are in a task
     */
    @Input() task?: PurchaseRequisitionTask;

    public articles!: DemandArticle[];

    public tomorrow: Dayjs;
    public demandValid = false;
    public validator?: DemandValidator;
    public TextModuleCategoryEnum = TextModuleCategoryEnum;

    internalMessageCtrl = new FormControl('');
    messageToSupplierCtrl = new FormControl('');
    conditionsCtrl = new FormControl('');

    /**
     * The id of the product which price is currently updating
     */
    public updatingProductQuantityFor?: string | null = null;
    public priceUpdater$: Subject<Article<DemandProduct>> = new Subject<Article<DemandProduct>>();

    public currency?: DisplayCurrency;
    /**
     * Currencies this demand may be written in and the currency it currently uses (EPROC-11425).
     * Steers the supplier selection and the validity of the demand.
     */
    public selectableCurrencies?: DemandSelectableCurrencies;
    /** Selectable currencies as a readable list for the conflict message; derived per lookup. */
    public selectableCurrencyList = '';
    /**
     * True when the customer itself does not allow the demand currency — the conflict banner then
     * names the customer instead of blaming the suppliers (EPROC-11425 reopen #2).
     */
    public customerRejectsDemandCurrency = false;
    /**
     * True when the lookup reports zero selectable currencies (empty customer-supplier
     * intersection). Blocks deliberately (decision 06.08.2026) — the banner must not dangle an
     * empty "selectable" list but name the dead end and the way out.
     */
    public noSelectableCurrencies = false;
    public PurchaseRequisitionStatusEnum = PurchaseRequisitionStatusEnum;
    public companySettings!: ICustomerCompanySettings;
    // Only enforced while the purchasing group is displayed — a field nobody can see must not block
    // the release.
    public purchasingGroupRequired = false;
    // The customer uses the purchasing group in the ordering process. Starts false so no purchasing
    // group can flash up before the setting has arrived.
    public purchasingGroupDisplayed = false;
    // The purchasing group may be offered to this reader even without a stored value: the customer
    // displays it and the reader administers purchasing groups.
    public purchasingGroupAvailable = false;

    public companyCodes: ICompanyCode[] = [];
    public selectedCompanyCodeId: string | null = null;
    /** Resolved "Code - Name" for the company code shown in the header (EPROC-11163). */
    public companyCodeHeaderLabel: string | null = null;
    /** Site-aware delivery-address union for the demand header (EPROC-10909). */
    public shippingCandidates: ShippingAddress[] = [];
    public companyCodeOptionsLoading = false;
    public companyCodeUpdating = false;
    public companyCodeLoadFailed = false;
    public recommendedClassificationIrdiccsByItemId: Record<string, readonly string[]> = {};
    public recommendedClassificationsByItemId: Record<string, readonly AccountingClassificationCandidate[]> = {};
    public recommendedClassificationSourceLabelsByItemId: Record<string, string | null> = {};
    public recommendedClassificationReasonLabelsByItemId: Record<string, string | null> = {};
    public recommendedMaterialGroupCodesByItemId: Record<string, readonly string[]> = {};
    public recommendedMaterialGroupSourceLabelsByItemId: Record<string, string | null> = {};
    public recommendedMaterialGroupReasonLabelsByItemId: Record<string, string | null> = {};

    public readonly AclResource = AclResource;
    public readonly AclAction = AclAction;
    public readonly accountingBaseDisplayType = AccountingBaseDisplayType;
    public readonly purchasingGroupLabel = marker('Einkaufsgruppe');

    public loading = false;
    /**
     * HTTP status of a failed demand load on the standalone route /client/rfq/demand/:demandId
     * (e.g. deleted demand behind a stale link). Rendered as an in-place error state by
     * base-site-layout instead of an endless loading spinner. Only the demand lookup itself feeds
     * this — a demand that loaded stays visible even if follow-up data fails. (EPROC-11534)
     */
    public readonly error = signal<HttpStatusCode | undefined>(undefined);
    public emailValidationErrors: string[] = [];
    public isCheckingEmail = false;

    /**
     * EPROC-11560: reactive state of the supplier currency check. Templates bind to its signals; the
     * ORDER release is only allowed once an answered check reported a compatible currency
     * (EPROC-11425/11560 Konsolidierung: harter Block nur Bestellweg).
     */
    public readonly supplierCurrencyStore = inject(SupplierCurrencyStore);
    public readonly pdfPreviewStore = inject(DemandPdfPreviewStore);
    /**
     * Sequence number of the running currency check. Supplier and currency changes can overlap, so only
     * the answer of the latest check is allowed to decide the release state (EPROC-11560).
     */
    private supplierCurrencyCheckToken = 0;
    /** Sequence number of the latest article rebuild after a demand change; only it may replace the list. */
    private articleRebuildToken = 0;
    /**
     * Signature (company code, supplier, document currencies) the currency store was last refreshed
     * for. `Demand.onChange` fires for every persisted change, not just ones that can affect
     * compatibility (EPROC-11560) — comparing signatures avoids re-checking, and briefly blocking the
     * release with no visible reason, on unrelated changes.
     */
    private lastSupplierCurrencyCheckSignature: string | null = null;

    private _currentlyLoggedInUser!: IUser;
    private _isCompleted = false;
    private companyCodeResetInProgress = false;

    get isCompleted(): boolean {
        return this._isCompleted;
    }

    public get resultsIn(): PurchaseRequisitionResult {
        if (this.purchaseRequisition) {
            return this.purchaseRequisition.resultsIn;
        }
        return PurchaseRequisitionResult.RFQ;
    }

    public get heading(): string {
        if (!this.demand?.isAnnounced) {
            return marker('Bedarf formulieren');
        }

        return marker('Bedarfsmeldung');
    }

    public get isPurchaseRequisition(): boolean {
        return !!this.purchaseRequisition;
    }

    public get isPending(): boolean {
        return (
            this.isPurchaseRequisition &&
            PurchaseRequisitionStatusEnum.PENDING === this.purchaseRequisition!.status.value
        );
    }

    /**
     * Whether the article-data validation banner (missing accountings, classifications, sites, …) is
     * still actionable and therefore worth showing. It is shown only while the line items can still
     * influence the outcome: during capture/editing of a not-yet-submitted demand, or while a
     * requisition is pending approval (so the approver recognizes missing accountings as a rejection
     * reason). Once the demand is read-only — after the approval process finished as
     * APPROVED/REJECTED/WITHDRAWN — the section can no longer be corrected, so the warning would be
     * actionless and misleading and is hidden. (EPROC-11218)
     */
    public get articleValidationActionable(): boolean {
        return this.isPending || (!this.isPurchaseRequisition && !this.demand?.readOnly);
    }

    public get showCompanyCodeSelection(): boolean {
        if (!this.demand || this.demand.readOnly || !this.companyCodes.length) {
            return false;
        }
        if (this.companyCodes.length === 1) {
            return !this.demand.companyCodeId;
        }
        return true;
    }

    public get requiresCompanyCodeSelection(): boolean {
        return !!this.demand && !this.demand.readOnly && !this.demand.companyCodeId;
    }

    public get demandReadOnly(): boolean {
        return !!this.demand?.readOnly || this.requiresCompanyCodeSelection;
    }

    public get accountingsEditable(): boolean {
        return !this.demandReadOnly && !this.companyCodeUpdating && !this.companyCodeResetInProgress;
    }

    /**
     * The "Kopfdaten" card is only rendered for an announced purchase requisition that has a consumer.
     * (EPROC-11163)
     */
    public get isHeaderDataVisible(): boolean {
        return this.isPurchaseRequisition && !!this.demand?.isAnnounced && !!this.demand?.consumer;
    }

    /**
     * In the requisition detail view the booking circle ("Buchungskreis") is shown inside the header
     * data instead of its own card, so the standalone card is suppressed there. Only relevant for users
     * with more than one company code; with a single one nothing is shown. During creation/editing the
     * selection dropdown card stays. (EPROC-11163)
     */
    public get showCompanyCodeInHeader(): boolean {
        return (
            this.isHeaderDataVisible &&
            this.demandReadOnly &&
            this.companyCodes.length > 1 &&
            !!this.demand?.companyCodeId
        );
    }

    /**
     * The expired-validity error is surfaced while the requisition is still being created or has not yet
     * been both approved and fully ordered. Once it is approved and ordered the validity window no longer
     * matters and the error is hidden. (EPROC-11163)
     */
    public get showValidUntilExpiredError(): boolean {
        if (!this.demand?.isAnnounced || !this.purchaseRequisition) {
            return true;
        }
        const approved = this.purchaseRequisition.status.value === PurchaseRequisitionStatusEnum.APPROVED;
        const ordered = this.purchaseRequisition.orderStatus === PurchaseRequisitionOrderStatus.FULLY_ORDERED;
        return !(approved && ordered);
    }

    constructor() {
        super();
        this.tomorrow = dayjs().add(1, 'd').endOf('d');
        this.priceUpdater$
            .pipe(
                tap((article: Article<DemandProduct>) => (this.updatingProductQuantityFor = article.original.itemId)),
                debounceTime(300),
                takeUntil(this.onDestroy$),
            )
            .subscribe((article: Article<DemandProduct>) => this.articlePriceChanged(article));
    }

    public async ngOnInit(): Promise<void> {
        this._currentlyLoggedInUser = await this.userService.getMyself();
        this.companySettings = await firstValueFrom(this.companySettingService.getSettings());
        await this.applyPurchasingGroupSettings(
            await this.aclService.getPermissionSync(AclResource.purchasingGroup, AclAction.SHOW),
        );
        // EPROC-11619: for a purchaseRequisition-bound instance, ngOnChanges already ran setDemand()
        // (Angular calls ngOnChanges before ngOnInit, and it fires on the very first binding too) -
        // calling it again here used to create a SECOND DemandValidator, discarding every per-item
        // validity report the first one had already collected. That went unnoticed as long as those
        // reporters (accounting-selection-v2 etc.) got nudged to re-validate later anyway; it started
        // showing once lib-site-selection - which normally never gets new inputs after mounting and so
        // only gets one chance to report - could mount early enough to land its report on the
        // now-discarded first validator instead of the one actually used afterwards.
        // EPROC-11619: for a purchaseRequisition-bound instance, ngOnChanges already ran setDemand()
        // (Angular calls ngOnChanges before ngOnInit, and it fires on the very first binding too) -
        // calling it again here used to create a SECOND DemandValidator, discarding every per-item
        // validity report the first one had already collected. That went unnoticed as long as those
        // reporters (accounting-selection-v2 etc.) got nudged to re-validate later anyway; it started
        // showing once lib-site-selection - which normally never gets new inputs after mounting and so
        // only gets one chance to report - could mount early enough to land its report on the
        // now-discarded first validator instead of the one actually used afterwards.
        if (!this.demandSetupStarted) {
            this.setDemand();
        }
        await this.loadShippingCandidates();
        if (this.purchaseRequisition) {
            this.purchaseRequisition.onChange = () => {
                this.validate();
                // EPROC-11425/11560 Konsolidierung: the selected result document decides whether the
                // currency verdict blocks (harter Block nur Bestellweg). The verdict itself does not
                // change with the result type, so no re-fetch — just re-scope the validation.
                this.applySupplierCurrencyState();
            };
        }
        this.internalMessageCtrl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(async (value) => {
            if (!this.demand) {
                return;
            }
            this.demand.internalMessage = value ?? '';
            await this.demand.saveMetaData();
        });
        this.messageToSupplierCtrl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(async (value) => {
            if (!this.demand) {
                return;
            }
            this.demand.messageToSupplier = value ?? '';
            await this.demand.saveMetaData();
        });
        this.conditionsCtrl.valueChanges
            .pipe(
                tap((value) => {
                    if (this.demand) {
                        this.demand.conditions = value ?? '';
                    }
                }),
                debounceTime(300),
                takeUntil(this.onDestroy$),
            )
            .subscribe(async () => {
                if (this.demand) {
                    await this.demand.saveMetaData();
                }
            });

        if (this.demand && !this.demand.isAnnounced) {
            this.validateSupplierEmails(this.demand.suppliers);
        }

        if (this.canSelectPurchasingGroup) {
            void this.ensurePurchasingGroupsLoaded();
        }

        this.loadLinkedDocuments();
        this.scheduleNextEagerLoadBatch();
    }

    private scheduleNextEagerLoadBatch(): void {
        this.eagerLoadTimeoutHandle = setTimeout(() => {
            const total = this.articles?.length ?? 0;
            if (total > 0 && this.eagerLoadCutoffIndex >= total) {
                // Fully unlocked: stop rescheduling, any later-added position (e.g. via add-product)
                // mounts immediately instead of waiting on a stagger that was only needed for the
                // initial page load.
                this.eagerLoadCutoffIndex = Number.MAX_SAFE_INTEGER;
                return;
            }
            this.eagerLoadCutoffIndex += DemandContentComponent.EAGER_LOAD_BATCH_SIZE;
            this.scheduleNextEagerLoadBatch();
        }, DemandContentComponent.EAGER_LOAD_INTERVAL_MS);
    }

    private loadLinkedDocuments(): void {
        const purchaseRequisitionId = this.purchaseRequisition?.id;
        if (!purchaseRequisitionId) {
            this.fetchedLinkedDocuments.set([]);
            this.linkedDocumentsLoading.set(false);
            return;
        }
        this.processChainDataService
            .getRelatedDocuments(ProcessChainDocumentType.PURCHASE_REQUISITION, purchaseRequisitionId)
            .pipe(finalize(() => this.linkedDocumentsLoading.set(false)))
            .subscribe({
                next: (docs) => this.fetchedLinkedDocuments.set(docs),
                error: (err) => console.error('Failed to load related documents', err),
            });
    }

    public navigateToLinkedDocument(linkedDocument: ProcessChainLinkedDocumentView): void {
        if (!linkedDocument.targetRoute) {
            return;
        }
        const extras: NavigationExtras | undefined =
            linkedDocument.documentType === ProcessChainDocumentType.DELIVERY_RECEIPT && linkedDocument.documentNumber
                ? { queryParams: { receiptNumber: linkedDocument.documentNumber } }
                : undefined;
        void this.router.navigate(linkedDocument.targetRoute, extras ?? {});
    }

    private resolveLinkedDocumentRoute(linkedDocument: ProcessChainRelatedDocument): string[] | null {
        switch (linkedDocument.documentType) {
            case ProcessChainDocumentType.ORDER:
                return [View.CLIENT, 'order', linkedDocument.documentId, 'detail'];
            case ProcessChainDocumentType.DELIVERY_RECEIPT:
                return [View.CLIENT, 'order', 'receipt', linkedDocument.documentId];
            case ProcessChainDocumentType.ORDER_CONFIRMATION:
                return [View.CLIENT, 'order', 'confirmation', linkedDocument.documentId];
            case ProcessChainDocumentType.INVOICE:
                return [View.CLIENT, 'my-admin', 'invoice', linkedDocument.documentId, 'detail'];
            case ProcessChainDocumentType.PURCHASE_REQUISITION:
                return [View.CLIENT, 'rfq', 'purchase-requisition', linkedDocument.documentId, 'detail'];
            case ProcessChainDocumentType.RFQ:
                return [View.CLIENT, 'rfq', linkedDocument.documentId, 'detail'];
            case ProcessChainDocumentType.QUOTATION:
                return [View.CLIENT, 'rfq', linkedDocument.documentId, 'quotation'];
            default:
                return null;
        }
    }

    public companyCodeLabel(companyCodeId: string | null | undefined): string {
        if (!companyCodeId) {
            return '-';
        }
        const matching = this.companyCodes.find((code) => code.id === companyCodeId);
        if (matching) {
            return `${matching.code} - ${matching.name}`;
        }
        return companyCodeId;
    }

    /**
     * Resolves the "Code - Name" shown for the company code in the header. Falls back to a direct
     * lookup when the viewing user is no longer assigned to that company code, so the header keeps
     * showing the readable label instead of just the raw id. (EPROC-11163)
     */
    private async updateCompanyCodeHeaderLabel(): Promise<void> {
        const companyCodeId = this.demand?.companyCodeId;
        if (!companyCodeId) {
            this.companyCodeHeaderLabel = null;
            return;
        }
        const fromActive = this.companyCodes.find((code) => code.id === companyCodeId);
        if (fromActive) {
            this.companyCodeHeaderLabel = `${fromActive.code} - ${fromActive.name}`;
            return;
        }
        try {
            const companyCode = await firstValueFrom(this.companyCodeDataService.getById(companyCodeId));
            this.companyCodeHeaderLabel = `${companyCode.code} - ${companyCode.name}`;
        } catch {
            this.companyCodeHeaderLabel = this.companyCodeLabel(companyCodeId);
        }
        this.cdr.detectChanges();
    }

    /**
     * Normalizes backend validation messages by replacing placeholders, removing duplicates,
     * and splitting multi-line messages into display-ready lines.
     */
    public otherErrorsMessages(message: string | null | undefined): string[] {
        if (!message) {
            return [];
        }
        let normalized = message.replace(/\r\n/g, '\n');
        const companyCodeLabel = this.demand?.companyCodeId ? this.companyCodeLabel(this.demand.companyCodeId) : '';
        if (companyCodeLabel) {
            normalized = normalized.replace(/\{\{companyCodeId\}\}/g, companyCodeLabel);
        } else {
            normalized = normalized.replace(/\{\{companyCodeId\}\}/g, '');
        }
        const unique: string[] = [];
        for (const line of normalized.split('\n')) {
            const cleaned = line.replace(/\s{2,}/g, ' ').trim();
            if (!cleaned) {
                continue;
            }
            if (!unique.includes(cleaned)) {
                unique.push(cleaned);
            }
        }
        return unique;
    }

    public async handleCompanyCodeSelection(companyCodeId: string | null): Promise<void> {
        if (!companyCodeId || !this.demand) {
            return;
        }
        if (companyCodeId === this.demand.companyCodeId) {
            return;
        }
        const previousResolutionSnapshot = this.createResolutionSnapshot();
        this.selectedCompanyCodeId = companyCodeId;
        this.companyCodeResetInProgress = true;
        try {
            const updated = await this.applyCompanyCode(companyCodeId);
            if (updated) {
                await this.removeSuppliersUnavailableForCompanyCode(companyCodeId);
                await this.reloadResolvedDemandState();
                if (previousResolutionSnapshot !== this.createResolutionSnapshot()) {
                    const infoMessage = marker('Kontierungen wurden aufgrund des Buchungskreiswechsels angepasst.');
                    this.notificationService.showNotification(infoMessage, NotificationMessageLevel.INFO);
                }
            }
        } finally {
            this.companyCodeResetInProgress = false;
        }
    }

    public upload(item: DemandProduct | null, internal?: boolean): void {
        this.bottomSheetService
            .upload({ uploadInternalPossible: internal === undefined, multiple: true })
            ?.afterDismissed()
            .subscribe(async (result?: UploadBottomSheetResult) => {
                if (result) {
                    const isInternal = internal !== undefined ? internal : result?.internal;
                    if (item) {
                        await item.addFile(result.formData, isInternal);
                    } else {
                        await this.demand.addFile(result.formData, isInternal);
                    }
                }
            });
    }

    public checkInitStatus(): void {
        // EPROC-11619: the accounting block per position is @defer'd (on viewport), so
        // accountingSelectionComponents only contains the positions mounted so far. Requiring its
        // length to match articles.length keeps this from firing "initialized" once just the
        // above-the-fold positions are ready.
        const accountingInitialized = this.accountingSelectionComponents
            ? this.accountingSelectionComponents.length === (this.articles?.length ?? 0) &&
              this.accountingSelectionComponents.toArray().every((component) => component.initializedState)
            : true;
        const additionalCostsInitialized = this.additionalCostComponents
            ? this.additionalCostComponents.toArray().every((component) => component.isInitialized)
            : true;

        if (this.demand && accountingInitialized && additionalCostsInitialized) {
            this.initialized.emit();
        }
    }

    public async updateAllAccountings(selection: IAccountingBulkChangeSelection): Promise<void> {
        const { changes, overwriteExisting, siteCode, purchasingGroupCode, materialGroupCode } = selection;

        if (!changes.length && !siteCode && !purchasingGroupCode && !materialGroupCode) {
            return;
        }

        const accountingRequests = changes.map((change) => ({
            type: change.type,
            customAccountingTypeId: change.customAccountingTypeId,
            _value: change.value
                ? { id: change.value.id, key: change.value.key, name: change.value.name, description: '' }
                : undefined,
        }));

        this.notificationService.spinner.show();
        try {
            const change = await firstValueFrom(
                this.dataService.applyAccountingToAllItems(
                    this.demand.id,
                    accountingRequests,
                    overwriteExisting,
                    siteCode,
                    purchasingGroupCode,
                    materialGroupCode,
                ),
            );
            await this.reloadResolvedDemandState(false);
            this.notifyIfShippingAddressWasReplaced(change);
        } finally {
            this.notificationService.spinner.hide();
        }
    }

    public applyAccountingValidation(itemId: string, isValid: boolean): void {
        this.validator?.setAccountingItemValidation(itemId, isValid);
        this.emitValidationChange();
    }

    public applyClassificationValidation(itemId: string, isValid: boolean): void {
        this.validator?.setClassificationItemValidation(itemId, isValid);
        this.emitValidationChange();
    }

    // EPROC-10907: hard enforcement of the required site setting on the demand/RFQ.
    public applySiteValidation(itemId: string, isValid: boolean): void {
        this.validator?.setSiteItemValidation(itemId, isValid);
        this.emitValidationChange();
    }

    public updateClassificationCandidates(
        itemId: string,
        candidates: readonly AccountingClassificationCandidate[],
    ): void {
        this.recommendedClassificationIrdiccsByItemId = {
            ...this.recommendedClassificationIrdiccsByItemId,
            [itemId]: candidates.map((candidate) => candidate.irdicc),
        };
        this.recommendedClassificationsByItemId = {
            ...this.recommendedClassificationsByItemId,
            [itemId]: candidates,
        };
    }

    public updateClassificationRecommendationSourceLabel(itemId: string, sourceLabel: string | null): void {
        this.recommendedClassificationSourceLabelsByItemId = {
            ...this.recommendedClassificationSourceLabelsByItemId,
            [itemId]: sourceLabel,
        };
    }

    public updateClassificationRecommendationReasonLabel(itemId: string, reasonLabel: string | null): void {
        this.recommendedClassificationReasonLabelsByItemId = {
            ...this.recommendedClassificationReasonLabelsByItemId,
            [itemId]: reasonLabel,
        };
    }

    public updateMaterialGroupCandidates(itemId: string, candidateIds: readonly string[]): void {
        this.recommendedMaterialGroupCodesByItemId = {
            ...this.recommendedMaterialGroupCodesByItemId,
            [itemId]: candidateIds,
        };
    }

    public updateMaterialGroupRecommendationSourceLabel(itemId: string, sourceLabel: string | null): void {
        this.recommendedMaterialGroupSourceLabelsByItemId = {
            ...this.recommendedMaterialGroupSourceLabelsByItemId,
            [itemId]: sourceLabel,
        };
    }

    public updateMaterialGroupRecommendationReasonLabel(itemId: string, reasonLabel: string | null): void {
        this.recommendedMaterialGroupReasonLabelsByItemId = {
            ...this.recommendedMaterialGroupReasonLabelsByItemId,
            [itemId]: reasonLabel,
        };
    }

    /**
     * Returns whether the approver may override the purchasing group of a demand item from within
     * this task (UC-EKG-13). True only when the task is the release task and assigned to the current user.
     */
    public get canEditPurchasingGroup(): boolean {
        if (!this.task || !this._currentlyLoggedInUser) {
            return false;
        }
        if (this.task.assignee !== this._currentlyLoggedInUser.aclUserId) {
            return false;
        }

        return (
            this.task.formKey === 'app:' + WorkflowFormKey.PURCHASE_REQUISITION_RELEASE_FORM ||
            this.task.formKey === 'app:' + WorkflowFormKey.PURCHASE_REQUISITION_APPROVE_OR_REWORK_FORM ||
            this.task.formKey === 'app:' + WorkflowFormKey.PURCHASE_REQUISITION_RELEASE_AND_CHOOSE_RESULT_FORM
        );
    }

    /**
     * Whether the purchasing group can be chosen for a position. Only the approver within the release
     * task may do so (UC-EKG-13); for the consumer it is derived from the material group and read-only,
     * including while reworking the demand inside a release process. A purchasing group the customer
     * does not display during the ordering process cannot be chosen at all.
     */
    public get canSelectPurchasingGroup(): boolean {
        return this.canEditPurchasingGroup && this.purchasingGroupDisplayed;
    }

    public purchasingGroupOptionsFor(article: DemandArticle): readonly IPurchasingGroupOption[] {
        const activeOptions = this.activePurchasingGroupOptions;
        const selectedCode = article.original.purchasingGroup?.code;
        const selectedOptionFromActive = selectedCode
            ? activeOptions.find((option) => option.code === selectedCode)
            : null;

        if (selectedCode && !selectedOptionFromActive) {
            const fallback = createPurchasingGroupFallbackOption(
                selectedCode,
                article.original.purchasingGroup?.name ?? selectedCode,
            );
            return [fallback, ...activeOptions];
        }

        return activeOptions;
    }

    public purchasingGroupSelectedCodeFor(article: DemandArticle): string | null {
        return article.original.purchasingGroup?.code ?? null;
    }

    public get purchasingGroupSelectPlaceholder(): string {
        // Read-only positions never load the selectable groups, so the empty/loading hints about them
        // would be misleading there.
        if (!this.canSelectPurchasingGroup) {
            return PURCHASING_GROUP_READONLY_PLACEHOLDER;
        }
        if (this.loadingPurchasingGroups) {
            return PURCHASING_GROUP_LOADING_PLACEHOLDER;
        }
        if (!this.activePurchasingGroupOptions.length) {
            return PURCHASING_GROUP_EMPTY_PLACEHOLDER;
        }
        return PURCHASING_GROUP_SELECT_PLACEHOLDER;
    }

    public get purchasingGroupDisabled(): boolean {
        return (
            this.canSelectPurchasingGroup &&
            (this.loadingPurchasingGroups || this.activePurchasingGroupOptions.length === 0)
        );
    }

    public get purchasingGroupDisabledMessage(): string | null {
        if (!this.canSelectPurchasingGroup) {
            return null;
        }
        if (this.loadingPurchasingGroups) {
            return PURCHASING_GROUP_LOADING_PLACEHOLDER;
        }
        if (this.purchasingGroupsLoadFailed) {
            return PURCHASING_GROUP_LOAD_FAILED_PLACEHOLDER;
        }
        if (!this.activePurchasingGroupOptions.length) {
            return PURCHASING_GROUP_EMPTY_PLACEHOLDER;
        }
        return null;
    }

    public async handlePurchasingGroupOptionSelected(
        option: IPurchasingGroupOption,
        article: DemandArticle,
    ): Promise<void> {
        await this.handlePurchasingGroupChanged(option.code, article);
    }

    public async handlePurchasingGroupCleared(article: DemandArticle): Promise<void> {
        const itemId = article.original.itemId;
        const taskId = this.task?.id;
        if (!itemId || !article.original.purchasingGroup || !taskId) {
            return;
        }

        this.notificationService.spinner.show();
        try {
            const updated = await firstValueFrom(
                this.dataService.deletePurchasingGroupForTask(taskId, this.demand.id, itemId),
            );
            this.applyPurchasingGroupUpdate(article, updated.purchasingGroup ?? null);
            this.notificationService.showNotification(
                marker('Einkaufsgruppe entfernt'),
                NotificationMessageLevel.SUCCESS,
            );
        } catch {
            this.notificationService.showNotification(
                marker('Einkaufsgruppe konnte nicht entfernt werden.'),
                NotificationMessageLevel.ERROR,
            );
        } finally {
            this.notificationService.spinner.hide();
        }
    }

    // Only the approver inside a release task may choose a group (UC-EKG-13), so this always runs
    // against the task endpoint.
    private async handlePurchasingGroupChanged(purchasingGroupCode: string, article: DemandArticle): Promise<void> {
        const itemId = article.original.itemId;
        const taskId = this.task?.id;
        if (!itemId || !purchasingGroupCode || !taskId) {
            return;
        }
        if (article.original.purchasingGroup?.code === purchasingGroupCode) {
            return;
        }

        this.notificationService.spinner.show();
        try {
            const updated = await firstValueFrom(
                this.dataService.updatePurchasingGroupForTask(taskId, this.demand.id, itemId, purchasingGroupCode),
            );
            this.applyPurchasingGroupUpdate(article, updated.purchasingGroup ?? null);
            this.notificationService.showNotification(
                marker('Einkaufsgruppe aktualisiert'),
                NotificationMessageLevel.SUCCESS,
            );
        } catch {
            this.notificationService.showNotification(
                marker('Einkaufsgruppe konnte nicht aktualisiert werden.'),
                NotificationMessageLevel.ERROR,
            );
        } finally {
            this.notificationService.spinner.hide();
        }
    }

    private applyPurchasingGroupUpdate(
        article: DemandArticle,
        purchasingGroup: { id: string; code: string; name: string } | null,
    ): void {
        article.original.purchasingGroup = purchasingGroup;
        article.purchasingGroupOptions = purchasingGroup ? [purchasingGroup] : [];
        this.validate();
    }

    private get activePurchasingGroupOptions(): readonly IPurchasingGroupOption[] {
        if (this.activePurchasingGroupOptionsCache?.source !== this.purchasingGroups) {
            const options = this.purchasingGroups
                .filter((group) => group.active)
                .map((group) => toPurchasingGroupOption(group))
                .sort((left, right) => left.label.localeCompare(right.label, 'de', { sensitivity: 'base' }));
            this.activePurchasingGroupOptionsCache = { source: this.purchasingGroups, options };
        }
        return this.activePurchasingGroupOptionsCache.options;
    }

    /**
     * Whether the customer uses the purchasing group at all is a customer setting, not a permission —
     * the settings endpoint needs no purchasing group administration right, so it is read for every
     * reader. The right only adds the offer of an empty field and the mandatory check on top. An
     * unreachable setting falls back to "displayed, not mandatory": it must neither hide a field that
     * is needed nor demand one that may not exist.
     */
    private async applyPurchasingGroupSettings(purchasingGroupAdministrator: boolean): Promise<void> {
        try {
            const settings = await firstValueFrom(this.purchasingGroupDataService.getSettings());
            this.purchasingGroupDisplayed = settings.showDuringOrderingProcess;
            this.purchasingGroupRequired =
                this.purchasingGroupDisplayed && purchasingGroupAdministrator && settings.required;
        } catch (error: unknown) {
            this.sentry.captureException(error);
            this.purchasingGroupDisplayed = true;
            this.purchasingGroupRequired = false;
        }
        this.purchasingGroupAvailable = purchasingGroupAdministrator && this.purchasingGroupDisplayed;
    }

    private async ensurePurchasingGroupsLoaded(): Promise<void> {
        if (this.purchasingGroupsLoaded || this.loadingPurchasingGroups) {
            return;
        }
        this.loadingPurchasingGroups = true;
        this.purchasingGroupsLoadFailed = false;
        try {
            this.purchasingGroups = await firstValueFrom(this.purchasingGroupDataService.getSelectable());
            this.purchasingGroupsLoaded = true;
        } catch {
            // Keep the failure distinct from "no active groups" so the field can show a truthful
            // hint instead of falsely claiming there are no active purchasing groups (EPROC-10971).
            this.purchasingGroupsLoadFailed = true;
        } finally {
            this.loadingPurchasingGroups = false;
        }
    }

    public async addSupplier(suppliers: DemandSupplier[]): Promise<void> {
        await this.demand.addSuppliers(suppliers);
        this.suppliersChanged.emit(this.demand.suppliers);
        if (!this.demand.isAnnounced) {
            this.validateSupplierEmails(this.demand.suppliers);
        }
        await this.refreshSelectableCurrencies();
    }

    public async removeSupplier(supplierCompanyId: string): Promise<void> {
        await this.demand.removeSupplier(supplierCompanyId);
        this.suppliersChanged.emit(this.demand.suppliers);

        if (!this.demand.isAnnounced) {
            if (this.demand.suppliers.length === 0) {
                this.emailValidationErrors = [];
            } else {
                this.validateSupplierEmails(this.demand.suppliers);
            }
        }

        this.validate();
        await this.refreshSelectableCurrencies();
    }

    public supplierChanged(): void {
        this.suppliersChanged.emit(this.demand.suppliers);
        this.validate();
        void this.refreshSelectableCurrencies();
    }

    public async changeSupplierContact(event: IDemandSupplierContactChange): Promise<void> {
        await this.demand.changeSupplierContact(event.supplier.companyId.value, event.contactId);
        this.suppliersChanged.emit(this.demand.suppliers);
        if (!this.demand.isAnnounced) {
            this.validateSupplierEmails(this.demand.suppliers);
        }
        this.validate();
    }

    private validateSupplierEmails(suppliers: DemandSupplier[]): void {
        if (!suppliers?.length || !this.demand?.id) {
            this.isCheckingEmail = false;
            return;
        }

        this.isCheckingEmail = true;
        this.emailValidationErrors = [];

        firstValueFrom(this.dataService.validateSupplierEmail(this.demand.id))
            .then((result: ISupplierEmailValidationResponse) => {
                this.isCheckingEmail = false;

                if (result.valid) {
                    this.emailValidationErrors = [];
                } else if (result.errorMessages && result.errorMessages.length > 0) {
                    this.emailValidationErrors = result.errorMessages.map((errorKey) =>
                        this.translateEmailValidationError(errorKey),
                    );
                } else {
                    this.emailValidationErrors = [];
                }

                this.validate();
            })
            .catch(() => {
                this.isCheckingEmail = false;
                this.emailValidationErrors = [];
            });
    }

    private validateExistingSuppliersEmails(): void {
        if (this.demand?.suppliers?.length) {
            this.validateSupplierEmails(this.demand.suppliers);
        }
    }

    private translateEmailValidationError(errorKey: string): string {
        switch (errorKey) {
            case SupplierEmailValidationMessage.SUPPLIER_EMAIL_NOT_VALID:
                return this.i18n.translateInstant(
                    'Anfrage kann nicht erstellt werden: Es ist keine E-Mail-Adresse beim Ansprechpartner des Lieferanten hinterlegt',
                );
            default:
                return this.i18n.translateInstant(errorKey);
        }
    }

    override async ngOnDestroy(): Promise<void> {
        super.ngOnDestroy();
        clearTimeout(this.eagerLoadTimeoutHandle);
        if (this.demand && !this.demand.withdrawn && !this.demand.convertDirectlyToRfq) {
            await this.demand.saveMetaData();
        }
    }

    public clearUnannounced(): void {
        if (!this.demand.isAnnounced) {
            this.demand.withdraw();
            this.dataService.clearCurrentUnannouncedDemand().subscribe(() => {
                this.router.navigate([View.CLIENT, 'rfq', 'purchase-requisition', 'list']);
            });
        }
    }

    public withdraw(): void {
        if (this.isPending && this.currentUserIsConsumer() && !this.demandReadOnly && !this._isCompleted) {
            this._isCompleted = true;
            this.withdrawn.emit();
        }
    }

    public createNewDemand(): void {
        if (!this._isCompleted) {
            this._isCompleted = true;
            this.dataService.createNewDemand(this.demand.id).subscribe({
                next: (demandId) => {
                    const successMessage = marker('Neuen Bedarf erstellt.');
                    this.notificationService.showNotification(successMessage, NotificationMessageLevel.SUCCESS);
                    this.router.navigate([View.CLIENT, 'rfq', 'demand', demandId.demandId]);
                },
                error: (error: unknown) => {
                    this._isCompleted = false;
                    this.notificationService.showNotification(
                        this.resolvePurchaseRequisitionActionErrorMessage(
                            error,
                            marker('Neuer Bedarf konnte nicht erstellt werden.'),
                        ),
                        NotificationMessageLevel.ERROR,
                    );
                },
            });
        }
    }

    public async createRfq(): Promise<void> {
        if (this.validator?.validForRfq && !this._isCompleted) {
            this._isCompleted = true;
            await this.demand.saveMetaData();
            this.demand.convertToRfq();
            this.dataService.convertToRfq(this.demand.id).subscribe({
                next: () => {
                    const successMessage = marker('Anfrage wurde erstellt.');
                    this.notificationService.showNotification(successMessage, NotificationMessageLevel.SUCCESS);
                    this.router.navigate([View.CLIENT, 'rfq', 'purchase-requisition', 'list']);
                },
                error: () => (this._isCompleted = false),
            });
        }
    }

    public async articlePriceChanged(article: Article<DemandProduct>): Promise<void> {
        if (this.demand) {
            const product: DemandProduct = article.original;
            const response: IDemandProductChangeResponse = await product.updateQuantity(article.quantity);
            this.demand.updatePriceInfo(response.demandPrice);
            const productPrice: IDemandProductPrice = response.changedProduct.price;
            article.price = new ArticlePrice(productPrice.currency, productPrice.unitPrice, productPrice.linePrice);
            article.price.taxPercent = productPrice.taxRate;
            article.price.taxPrice = productPrice.taxAmount;
            this.updatingProductQuantityFor = null;
        }
    }

    public async updateAccounting(event: AccountingChange, itemId: string, reloadState = true): Promise<void> {
        const article: DemandArticle | undefined = this.articles.find((a) => a.id === itemId);
        if (!article) {
            return;
        }

        await article.applyAccountingChange(this.demand.id, ArticleAccountingChangeEvent.from(event, itemId));

        // EPROC-11619: applyAccountingChange() already persisted the change and refreshed only this
        // article's own accountingInput. A full reloadResolvedDemandState() here would re-fetch
        // accounting/classification for every other position too and hand all of them a new
        // accountingInput reference, re-triggering their ngOnChanges for no reason - an O(N) request
        // storm on every single field change. validate() is enough to reflect the change (e.g.
        // accounting-completeness) in the form state.
        if (reloadState) {
            this.validate();
        }
    }

    /**
     * Company code the per-item site selector reacts to. Deliberately the server-confirmed
     * `demand.companyCodeId`, not the optimistic `selectedCompanyCodeId`: the selector auto-persists a site
     * as soon as it sees a company code, so exposing a not-yet-saved change would make it write a site the
     * backend still rejects ("Site does not belong to the document's company code").
     */
    public get siteSelectionCompanyCodeId(): string {
        return this.demand?.companyCodeId ?? '';
    }

    // EPROC-10907: persist the customer site (WERKS) selected on a demand item
    public async updateSite(event: ISiteChange | null, itemId: string): Promise<void> {
        const change = await firstValueFrom(
            this.dataService.updateItemSite(this.demand.id, itemId, event ? event.siteCode : null),
        );
        this.demand.applyShippingAddressChange(change);

        // EPROC-11619: only the shipping-address union depends on a site change (EPROC-10909); it is
        // its own single request, not per-position, so it does not need the full article rebuild that
        // reloadResolvedDemandState() would otherwise trigger for every other position.
        const article = this.articles.find((a) => a.original.itemId === itemId);
        if (article) {
            article.original.siteCode = event ? event.siteCode : null;
        }
        this.validate();
        await this.loadShippingCandidates();
        this.notifyIfShippingAddressWasReplaced(change);
    }

    public async updateValidUntil($event: Dayjs | null): Promise<void> {
        if (!$event) {
            // EPROC-10809: "Gueltig bis" ist Pflicht fuer den Anfrageversand und darf nicht leer bleiben.
            // Der iim-datepicker leert beim Klick auf den Muelleimer nur seine eigene Anzeige, das Modell
            // bleibt gesetzt - ohne das Zurueckschreiben hier zeigt das Feld leer, waehrend die
            // Bedarfsmeldung weiterhin ein Datum traegt. Zurueckgesetzt wird auf morgen, den
            // fruehestmoeglichen Termin (= [minDate] des Datepickers) - dieselbe Korrektur, die ein
            // Datum in der Vergangenheit ausloest.
            this.notificationService.showNotification(
                marker('Die Gültigkeit ist ein Pflichtfeld und wurde zurückgesetzt.'),
                NotificationMessageLevel.WARNING,
            );
            await this.updateValidUntil(dayjs().add(1, 'd').endOf('d'));

            return;
        }

        if ($event.isBefore(dayjs())) {
            this.notificationService.showNotification(
                marker('Das Datum muss in der Zukunft liegen!'),
                NotificationMessageLevel.WARNING,
            );
            await this.updateValidUntil(dayjs().add(1, 'd').endOf('d')); // set to tomorrow
        } else {
            this.demand.validUntil = $event;
            await this.demand.saveMetaData();
            this.emitValidationChange();
        }
    }

    public async updateClassification(
        article: DemandArticle,
        classification: ClassificationCode | undefined,
    ): Promise<void> {
        try {
            // EPROC-11619: applyClassificationChange() already persists the change and updates only
            // this article's own classification field, so a full reloadResolvedDemandState() - which
            // would re-fetch accounting/classification for every other position too - is not needed.
            await article.applyClassificationChange(classification);
            this.validate();
        } catch {
            this.notificationService.showNotification(
                marker('Klassifikation konnte nicht gespeichert werden.'),
                NotificationMessageLevel.WARNING,
            );
        }
    }

    public async updateMaterialGroup(article: DemandArticle, materialGroupCode: string | undefined): Promise<void> {
        const previousMaterialGroupId = article.original.materialGroupCode;
        article.original.materialGroupCode = materialGroupCode ?? null;

        try {
            // EPROC-11619: save() already returns the server-resolved product for just this position
            // (including a possibly re-derived purchasing group), so apply that locally instead of
            // reloading and re-fetching every other position's accounting/classification state too.
            const response = await article.original.save();
            this.applyPurchasingGroupUpdate(article, response.changedProduct.purchasingGroup ?? null);
        } catch {
            article.original.materialGroupCode = previousMaterialGroupId;
            this.notificationService.showNotification(
                marker('Warengruppe konnte nicht gespeichert werden.'),
                NotificationMessageLevel.WARNING,
            );
            this.cdr.detectChanges();
        }
    }

    public async editArticle(article: Article<DemandProduct>): Promise<void> {
        await this.demand.saveMetaData();
        if (this.editingWithDialog) {
            this.dialog
                .open<DemandEditProductComponent, DemandEditProductDialogData>(
                    DemandEditProductComponent,
                    DialogOptions.LARGE(new DemandEditProductDialogData(this.demand, this.resultsIn, article.id)),
                )
                .afterClosed()
                .subscribe(async (demand?: Demand) => {
                    if (demand) {
                        try {
                            // EPROC-11619: demand.refresh() replaces demand.products wholesale with new
                            // DemandProduct instances (needed to pick up the recomputed aggregate price/
                            // additional costs), so every article's `original` has to be re-pointed at its
                            // fresh instance - but only the edited article's own display fields (name/
                            // price/etc., snapshotted once by DemandArticle.from()) are actually stale, so
                            // only that one needs its accounting/classification re-fetched, not all N via
                            // a full init()/getProductsAsArticles() rebuild.
                            await this.demand.refresh();
                            this.currency = new DisplayCurrency(this.demand.currency, this.getUserLanguage());
                            this.articles.forEach((a) => {
                                const freshProduct = this.demand.getProduct(a.original.itemId!);
                                if (freshProduct) {
                                    a.original = freshProduct;
                                }
                            });
                            const editedIndex = this.articles.findIndex((a) => a.id === article.id);
                            if (editedIndex !== -1) {
                                this.articles[editedIndex] = await DemandArticle.from(
                                    this.demand.id,
                                    this.articles[editedIndex].original,
                                    this.itemAccountingDataService,
                                );
                            }
                            this.validate();
                            // EPROC-11560: an edited price can change its currency, which can flip the
                            // currency compatibility with the selected supplier.
                            await this.refreshSelectableCurrencies();
                            this.cdr.detectChanges();
                        } catch {
                            this.notificationService.showNotification(
                                marker('Artikel konnte nicht aktualisiert werden.'),
                                NotificationMessageLevel.WARNING,
                            );
                        }
                    }
                });
        } else {
            this.router.navigate([View.CLIENT, 'rfq', 'demand', this.demand.id, 'edit', article.id], {
                queryParams: { [RESULTS_IN]: this.resultsIn },
            });
        }
    }

    public addProduct(productId?: string): void {
        if (this.editingWithDialog) {
            // The dialog works on this demand instance, whose change notification rebuilds the article list once
            // a new position is complete. Escape and backdrop clicks close it without a result, so whether
            // positions were added is read from the demand itself.
            const productCountBefore = this.demand.products.length;
            this.dialog
                .open<DemandAddProductComponent, DemandAddProductDialogData>(
                    DemandAddProductComponent,
                    DialogOptions.LARGE(new DemandAddProductDialogData(this.demand, this.resultsIn, productId)),
                )
                .afterClosed()
                .subscribe(async () => {
                    if (this.demand.products.length !== productCountBefore) {
                        this.validate();
                        await this.refreshShippingCandidatesAfterProductChange();
                    }
                });
        } else {
            const navigationExtras: NavigationExtras = {
                queryParams: {
                    [RESULTS_IN]: this.resultsIn,
                },
            };
            if (productId) {
                navigationExtras.queryParams!['copy'] = productId;
            }
            this.router.navigate([View.CLIENT, 'rfq', 'demand', this.demand.id, 'add-product'], navigationExtras);
        }
    }

    public async announceDemand(): Promise<void> {
        if (this.demand && this.validator?.validForNextStep() && !this._isCompleted && !this.loading) {
            await this.demand.saveMetaData();
            this.loading = true;
            await this.demand.announce();
            this.loading = false;
            this._isCompleted = this.demand.isAnnounced;
            if (this.demand.isAnnounced) {
                const message: string = marker('Bedarfsmeldung wurde erstellt!');
                this.notificationService.showNotification(message, NotificationMessageLevel.SUCCESS);
                if (await this.dataService.showTaskAfterDemandAnnouncement().toPromise()) {
                    await this.router.navigate([View.CLIENT, 'rfq', 'demand', this.demand.id, 'post-announce-task'], {
                        skipLocationChange: true,
                    });
                } else {
                    await this.router.navigateWithinView('rfq', 'purchase-requisition', 'list');
                }
            }
        }
    }

    public async removeProduct(article: Article<DemandProduct>): Promise<void> {
        if (this.demand && article.original.itemId) {
            this.notificationService.spinner.show();
            try {
                const change = await this.demand.removeProduct(article.original.itemId);
                // EPROC-11619: Demand.removeProduct() already removed the product from demand.products;
                // mirror that locally instead of re-fetching accounting/classification for every
                // remaining position via getProductsAsArticles().
                this.articles = this.articles.filter((a) => a.original.itemId !== article.original.itemId);
                this.validator?.removeItemValidation(article.original.itemId);
                this.validate();
                await this.refreshShippingCandidatesAfterProductChange();
                this.notifyIfShippingAddressWasReplaced(change);
            } catch {
                this.notificationService.showNotification(
                    marker('Artikel konnte nicht entfernt werden.'),
                    NotificationMessageLevel.WARNING,
                );
            } finally {
                this.notificationService.spinner.hide();
            }
        }
    }

    public get canBulkEditDeliveryDate(): boolean {
        return (
            !!this.demand &&
            !this.demandReadOnly &&
            this.demand.products.length > 0 &&
            this.demand.products.every((product) => !!product.itemId)
        );
    }

    public openUpdateDeliveryDateDialog(): void {
        if (!this.canBulkEditDeliveryDate) {
            return;
        }

        this.dialog
            .open<
                DemandUpdateDeliveryDateDialogComponent,
                DemandUpdateDeliveryDateDialogData,
                DemandUpdateDeliveryDateDialogResult
            >(
                DemandUpdateDeliveryDateDialogComponent,
                DialogOptions.SMALL(new DemandUpdateDeliveryDateDialogData(dayjs())),
            )
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(async (result) => {
                if (result?.desiredDeliveryDate) {
                    const successMessage = marker('Liefertermin wurde für alle Positionen aktualisiert.');
                    const errorMessage = marker('Liefertermin konnte nicht aktualisiert werden.');
                    this.notificationService.spinner.show();
                    try {
                        await this.demand.updateDeliveryDateForAllItems(result.desiredDeliveryDate);
                        this.articles = await this.demand.getProductsAsArticles(this.itemAccountingDataService);
                        this.clearRecommendationState();
                        this.validate();
                        this.notificationService.showNotification(successMessage, NotificationMessageLevel.SUCCESS);
                    } catch {
                        this.notificationService.showNotification(errorMessage, NotificationMessageLevel.WARNING);
                    } finally {
                        this.notificationService.spinner.hide();
                    }
                }
            });
    }

    public currentUserIsConsumer(): boolean {
        return this._currentlyLoggedInUser.id === this.demand.consumer?.id;
    }

    /**
     * EPROC-11534: maps a demand load failure onto the HTTP status rendered by base-site-layout's
     * error state. Demand.byId wraps HTTP errors in UnableToRetreiveDemandError; demand.refresh()
     * throws the raw HttpErrorResponse — everything else counts as an internal error. Status 0
     * (network/CORS/abort) is mapped as well: it is falsy and would fall back to the spinner branch
     * of base-site-layout instead of showing an error.
     */
    private resolveDemandLoadErrorCode(error: unknown): HttpStatusCode {
        const status =
            error instanceof UnableToRetreiveDemandError || error instanceof HttpErrorResponse ? error.status : 0;

        return status > 0 ? status : HttpStatusCode.InternalServerError;
    }

    private resolvePurchaseRequisitionActionErrorMessage(error: unknown, fallbackMessage: string): string {
        if (error instanceof HttpErrorResponse) {
            if (error.status === 403) {
                return marker('Sie haben keine Berechtigung für diese Aktion.');
            }
            if (error.status === 404) {
                return marker('Die Bedarfsmeldung wurde nicht gefunden.');
            }
        }

        return fallbackMessage;
    }

    public getUserLanguage(): string {
        return this._currentlyLoggedInUser?.language?.iso639LanguageAlpha2 ?? this.i18n.currentLanguage;
    }

    public validUntilValid(): boolean {
        if (this.isPurchaseRequisition) {
            return !this.validUntilRequired || (!!this.demand.validUntil && this.demand.validUntil.isValid());
        }
        return true;
    }

    public suppliersValid(): boolean {
        return (
            (!this.supplierRequired || !!this.demand.suppliers.length) && this.demand.suppliers.every((s) => s.valid)
        );
    }

    /**
     * The demand loads and replaces its attachments outside of Angular's change tracking, so the PDF
     * preview of an editable draft is synchronised on every check.
     */
    ngDoCheck(): void {
        const editableDraft = !!this.demand && !this.isPurchaseRequisition && !this.demand.readOnly;
        this.pdfPreviewStore.syncFiles(editableDraft ? (this.demand.files ?? []) : []);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['purchaseRequisition']) {
            this.setDemand();
            if (!changes['purchaseRequisition'].isFirstChange()) {
                this.loadLinkedDocuments();
            }
        }
        if (changes['history'] && this.history !== undefined) {
            this.historyLoaded.set(true);
        }
        if (changes['task'] && this.canSelectPurchasingGroup) {
            // The approver may have just claimed the task; reload the active purchasing groups so
            // the dropdown switches from read-only to editable without a page refresh.
            void this.ensurePurchasingGroupsLoaded();
        }
    }

    private prepareCompanyCodes(): void {
        if (!this.demand) {
            return;
        }
        this.companyCodeOptionsLoading = true;
        this.companyCodeLoadFailed = false;
        this.companyCodeDataService
            .getMyActive()
            .pipe(
                finalize(() => {
                    this.companyCodeOptionsLoading = false;
                }),
            )
            .subscribe({
                next: (companyCodes) => {
                    this.companyCodes = [...companyCodes].sort((a, b) => a.code.localeCompare(b.code));
                    if (!this.demand?.readOnly && this.companyCodes.length === 1 && !this.demand?.companyCodeId) {
                        void this.handleCompanyCodeSelection(this.companyCodes[0].id);
                    } else {
                        this.selectedCompanyCodeId = this.demand?.companyCodeId ?? null;
                    }
                    void this.updateCompanyCodeHeaderLabel();
                },
                error: () => {
                    this.companyCodes = [];
                    this.companyCodeLoadFailed = true;
                    this.notificationService.showNotification(
                        marker('Fehler beim Laden der Buchungskreise'),
                        NotificationMessageLevel.WARNING,
                    );
                    this.selectedCompanyCodeId = this.demand?.companyCodeId ?? null;
                    void this.updateCompanyCodeHeaderLabel();
                },
            });
    }

    private async applyCompanyCode(companyCodeId: string): Promise<boolean> {
        if (!this.demand) {
            return false;
        }
        this.companyCodeUpdating = true;
        try {
            const change = await this.demand.updateCompanyCode(companyCodeId);
            this.selectedCompanyCodeId = companyCodeId;
            this.notifyIfShippingAddressWasReplaced(change);
            return true;
        } catch {
            const message = marker('Buchungskreis konnte nicht aktualisiert werden.');
            this.notificationService.showNotification(message, NotificationMessageLevel.WARNING);
            this.selectedCompanyCodeId = this.demand.companyCodeId ?? null;
            return false;
        } finally {
            this.companyCodeUpdating = false;
        }
    }

    private async removeSuppliersUnavailableForCompanyCode(companyCodeId: string): Promise<void> {
        if (!this.demand?.suppliers?.length) {
            return;
        }
        try {
            const availableSuppliers = await firstValueFrom(this.supplierSearchService.search('', true, companyCodeId));
            const availableSupplierIds = new Set(availableSuppliers.map((supplier) => supplier.id));
            const suppliersToRemove = this.demand.suppliers.filter(
                (supplier) => !availableSupplierIds.has(supplier.companyId.value),
            );
            if (!suppliersToRemove.length) {
                return;
            }
            for (const supplier of suppliersToRemove) {
                await this.removeSupplier(supplier.companyId.value);
            }
            const infoMessage = marker(
                'Lieferanten wurden entfernt, da sie im ausgewählten Buchungskreis nicht aktiv sind.',
            );
            this.notificationService.showNotification(infoMessage, NotificationMessageLevel.INFO);
        } catch {
            const warningMessage = marker('Verfügbare Lieferanten konnten nicht geprüft werden.');
            this.notificationService.showNotification(warningMessage, NotificationMessageLevel.WARNING);
        }
    }

    private async reloadResolvedDemandState(showSpinner = true): Promise<void> {
        if (!this.demand) {
            return;
        }

        if (showSpinner) {
            this.notificationService.spinner.show();
        }
        try {
            await this.demand.refresh();
            this.selectedCompanyCodeId = this.demand.companyCodeId ?? null;
            this.articles = await this.demand.getProductsAsArticles(this.itemAccountingDataService);
            this.clearRecommendationState();
            this.validate();
            // EPROC-11560: a price, supplier or company-code change can flip the currency compatibility.
            await this.refreshSelectableCurrencies();
            // EPROC-10909: a position site (or company code) change can change the delivery-address union.
            await this.loadShippingCandidates();
            this.cdr.detectChanges();
            this.refreshAccountingOptionSources();
        } finally {
            if (showSpinner) {
                this.notificationService.spinner.hide();
            }
        }
    }

    /**
     * Loads the site-aware delivery-address union for the selection list. Choosing and replacing the address
     * is the server's job — it derives it from the position sites on every site change and records whether
     * the system or the user put it there. What the client still reports is an address the user chose that
     * has dropped out of the list without a site change of this demand (an assignment withdrawn, an address
     * deactivated), because nothing else would tell the user.
     */
    private async loadShippingCandidates(): Promise<void> {
        if (!this.demand) {
            return;
        }

        const result = await firstValueFrom(this.deliveryAddressCandidatesDataService.loadForDemand(this.demand.id));
        this.shippingCandidates = result.candidates.map(candidateToShippingAddress);
        if (this.demand.readOnly) {
            return;
        }
        this.warnIfChosenShippingAddressDroppedOut();
    }

    private warnIfChosenShippingAddressDroppedOut(): void {
        const current = this.demand.shippingAddress;
        const currentId = current && !current.isEmpty() ? current.id : undefined;
        // Free addresses carry no id and are never measured against the list; a system-derived address is
        // replaced by the server itself.
        const isChosenMasterAddress =
            this.demand.shippingAddressOrigin === DemandShippingAddressOrigin.USER && currentId != null && currentId > 0;
        if (
            !isChosenMasterAddress ||
            this.shippingCandidates.length === 0 ||
            this.shippingCandidates.some((address) => address.id === currentId)
        ) {
            return;
        }
        this.notificationService.showNotification(
            marker('Die gewählte Lieferadresse ist für die aktuellen Standorte nicht mehr verfügbar.'),
            NotificationMessageLevel.WARNING,
        );
    }

    private notifyIfShippingAddressWasReplaced(change: DemandShippingAddressChange | null): void {
        if (change?.replaced) {
            this.notificationService.showNotification(
                marker('Die bisherige Lieferadresse ist nicht mehr wählbar und wurde ersetzt.'),
                NotificationMessageLevel.WARNING,
            );
        }
    }

    private async refreshShippingCandidatesAfterProductChange(): Promise<void> {
        try {
            await this.loadShippingCandidates();
        } catch {
            this.notificationService.showNotification(
                marker('Verfügbare Lieferadressen konnten nicht aktualisiert werden.'),
                NotificationMessageLevel.WARNING,
            );
        }
    }

    private createResolutionSnapshot(): string {
        const articleSnapshot = (this.articles ?? []).map((article) => ({
            id: article.id,
            classificationIrdicc: article.classification?.irdicc ?? null,
            materialGroupCode: article.original.materialGroupCode ?? null,
            costCenter: article.costCenter || null,
            generalLedger: article.account || null,
            project: article.project || null,
            customAccounts: [...article.customAccountAssignments]
                .map((assignment) => ({
                    typeId: assignment.typeId,
                    key: assignment.key,
                }))
                .sort((left, right) => left.typeId - right.typeId),
        }));

        const additionalCostSnapshot =
            this.additionalCostComponents?.toArray().map((component) => {
                const accountings = component.accountings();

                return {
                    id: component.additionalCost().id,
                    costCenter: accountings.costCenterKey || null,
                    generalLedger: accountings.generalLedgerAccountKey || null,
                    project: accountings.projectAccountKey || null,
                    customAccounts: [...accountings.customAccountAssignments]
                        .map((assignment) => ({
                            typeId: assignment.typeId,
                            key: assignment.key,
                        }))
                        .sort((left, right) => left.typeId - right.typeId),
                };
            }) ?? [];

        return JSON.stringify({
            articles: articleSnapshot,
            additionalCosts: additionalCostSnapshot,
        });
    }

    /**
     * Only call right after rebuilding every article: each position then resolves again and re-reports
     * its recommendations. Anywhere else the positions do not re-resolve, so their recommendations would
     * be lost for good. Site, add and remove changes fire Demand.onChange, whose handler rebuilds.
     */
    private clearRecommendationState(): void {
        this.recommendedClassificationIrdiccsByItemId = {};
        this.recommendedClassificationsByItemId = {};
        this.recommendedClassificationSourceLabelsByItemId = {};
        this.recommendedClassificationReasonLabelsByItemId = {};
        this.recommendedMaterialGroupCodesByItemId = {};
        this.recommendedMaterialGroupSourceLabelsByItemId = {};
        this.recommendedMaterialGroupReasonLabelsByItemId = {};
    }

    private refreshAccountingOptionSources(): void {
        this.accountingSelectionComponents?.forEach((component) => component.refreshOptions());
        this.additionalCostComponents?.forEach((component) => component.refreshAccountingOptions());
    }

    /**
     * EPROC-11425/11560 Konsolidierung: ONE lookup (`GET /demand/{id}/selectable-currencies`) serves
     * the currency banner, the supplier filter and the supplier-currency store that gates the ORDER
     * release. The currency a demand may use depends on its suppliers, and which suppliers are
     * selectable depends on its currency — both are re-read whenever positions, suppliers or the
     * company code change, so dropdown, supplier search and release button stay in sync instead of
     * only failing at the end of the conversion.
     *
     * <p>The check token drops a superseded response (remove supplier A, immediately add B), so a
     * slower, older answer can never overwrite the newest verdict.
     */
    private async refreshSelectableCurrencies(): Promise<void> {
        const token = ++this.supplierCurrencyCheckToken;

        if (!this.demand?.id) {
            return;
        }

        // Only a check that actually ran consumes the signature. Set before the early return, a
        // skipped run would burn it: handleDemandChanged compares against exactly this value and
        // would never re-run the lookup, leaving the store PENDING for good (EPROC-11425 reopen #5b).
        this.lastSupplierCurrencyCheckSignature = this.buildSupplierCurrencyCheckSignature();

        // Block the ORDER release while the check runs, so a stale verdict cannot keep it enabled.
        // Announce and RFQ stay unaffected: applySupplierCurrencyState scopes the block to orders.
        this.supplierCurrencyStore.markPending();
        this.applySupplierCurrencyState();

        try {
            const selectable = await firstValueFrom(this.dataService.getSelectableCurrencies(this.demand.id));
            this.applySelectableCurrencies(selectable, token);
        } catch {
            this.applySupplierCurrencyCheckFailure(token);
        }
    }

    /** Applies the newest lookup result: banner state, store verdict, output for the approval form. */
    private applySelectableCurrencies(selectable: DemandSelectableCurrencies, token: number): void {
        // Latest check wins: a slower, older answer must not overwrite a newer verdict.
        if (token !== this.supplierCurrencyCheckToken) {
            return;
        }
        this.selectableCurrencies = selectable;
        // Derived once per lookup instead of in template getters: the conflict banner sits in a
        // @for-heavy template, and a join per change-detection run is pure allocation noise.
        this.selectableCurrencyList = selectable.currencies.join(', ');
        this.customerRejectsDemandCurrency = selectable.customerRejects(selectable.demandCurrency);
        this.noSelectableCurrencies = selectable.currencies.length === 0;
        this.supplierCurrencyStore.applyResult(selectable, this.demand?.suppliers?.[0]?.companyName ?? null);
        this.currencyAcceptanceChanged.emit(selectable);
        this.applySupplierCurrencyState();
        this.validate();
    }

    /**
     * A failed lookup must not block editing, announcing or the RFQ conversion — the backend still
     * rejects on release. Only the ORDER path fails closed (EPROC-11560), surfaced with a retry.
     */
    private applySupplierCurrencyCheckFailure(token: number): void {
        if (token !== this.supplierCurrencyCheckToken) {
            return;
        }
        this.selectableCurrencies = undefined;
        this.selectableCurrencyList = '';
        this.customerRejectsDemandCurrency = false;
        this.noSelectableCurrencies = false;
        this.supplierCurrencyStore.markFailed();
        this.currencyAcceptanceChanged.emit(undefined);
        this.applySupplierCurrencyState();
        this.validate();
    }

    /** Currency of the demand, or null while it has no position (EPROC-11425). */
    public get demandCurrency(): string | null {
        return this.selectableCurrencies?.demandCurrency ?? null;
    }

    /** True when the demand uses a currency its suppliers do not accept (EPROC-11425). */
    public get currencyConflict(): boolean {
        return !!this.selectableCurrencies && !this.selectableCurrencies.accepted;
    }

    private async init(): Promise<void> {
        this.prepareCompanyCodes();
        this.validator = DemandValidator.createPreservingItemReports(this.validator, this.demand, {
            validUntilRequired: this.validUntilRequired,
            suppliersRequired: this.supplierRequired,
            supplierArticleNumberRequired: !!this.companySettings?.supplierArticleNumberRequired,
            deliveryDateRequired: !!this.companySettings?.deliveryDateRequired,
            purchasingGroupRequired: this.purchasingGroupRequired,
        });
        this.articles = await this.demand.getProductsAsArticles(this.itemAccountingDataService);
        this.articlesLoaded.set(true);
        this.clearRecommendationState();
        this.validate();
        await this.refreshSelectableCurrencies();
        this.demand.onChange = () => this.handleDemandChanged();
        this.messageToSupplierCtrl.setValue(this.demand.messageToSupplier);
        this.internalMessageCtrl.setValue(this.demand.internalMessage);
        this.conditionsCtrl.setValue(this.demand.conditions, { emitEvent: false });

        // Validate existing suppliers' emails on init
        if (this.demand.suppliers && this.demand.suppliers.length > 0) {
            this.validateExistingSuppliersEmails();
        }

        this.cdr.detectChanges();
    }

    private validate() {
        this.validator?.update();
        this.cdr.detectChanges();
        this.emitValidationChange();
    }

    /** Retries a failed currency lookup from the supplier error message. */
    public retrySupplierCurrencyCompatibility(): void {
        void this.refreshSelectableCurrencies();
    }

    /**
     * Signature of the fields the supplier-currency compatibility can actually depend on: the company
     * code, the (single) supplier, and the document currencies in use (EPROC-11560).
     */
    private buildSupplierCurrencyCheckSignature(): string {
        if (!this.demand) {
            return '';
        }
        const supplierIds = (this.demand.suppliers ?? []).map((supplier) => supplier.companyId?.value ?? '').join(',');
        const currencies = Array.from(
            new Set((this.demand.products ?? []).map((product) => product.price?.currency ?? '')),
        ).join(',');
        return `${this.demand.companyCodeId ?? ''}|${supplierIds}|${currencies}`;
    }

    /**
     * True when the flow this demand lives in ends in an ORDER — the only flow the supplier-currency
     * verdict may hard-block (EPROC-11425/11560 Konsolidierung: harter Block nur Bestellweg,
     * decision 06.08.2026). Without a purchase requisition (plain "Bedarf melden") it never blocks.
     */
    private get resultsInOrder(): boolean {
        return this.purchaseRequisition?.resultsIn === PurchaseRequisitionResult.ORDER;
    }

    /** Conflict reason for the supplier card — only while it actually blocks the order path. */
    public get supplierCurrencyConflictMessage(): string | null {
        return this.resultsInOrder ? this.supplierCurrencyStore.conflictMessage() : null;
    }

    /** Technical check failure with retry for the supplier card — only relevant on the order path. */
    public get supplierCurrencyCheckErrorMessage(): string | null {
        return this.resultsInOrder ? this.supplierCurrencyStore.checkErrorMessage() : null;
    }

    /**
     * True while the currency check is still running on the order path. PENDING blocks the release
     * exactly like a conflict does, but used to be the only store state without a visible form —
     * the approver saw a dead release button with no reason (EPROC-11425 reopen #5b, 238308).
     */
    public get supplierCurrencyCheckPending(): boolean {
        return this.resultsInOrder && this.supplierCurrencyStore.status() === SupplierCurrencyCheckStatus.PENDING;
    }

    private applySupplierCurrencyState(): void {
        // EPROC-11425/11560 Konsolidierung: harter Block nur Bestellweg — outside an order-bound
        // flow the verdict never blocks, whatever the store's status is (pending/conflict/error).
        this.validator?.setSupplierCurrencyValidation(
            this.supplierCurrencyStore.releaseAllowed() || !this.resultsInOrder,
        );
        this.emitValidationChange();
        this.cdr.detectChanges();
    }

    /**
     * EPROC-11560: reacts to every persisted change of the demand. Company code, supplier and price
     * changes can change the supplier-currency verdict, so the lookup is re-run here — but only when
     * one of those fields actually changed, not on every unrelated change (e.g. a comment), which
     * would otherwise briefly and invisibly block the order release for the duration of the request.
     */
    private async handleDemandChanged(): Promise<void> {
        this.validate();
        if (this.buildSupplierCurrencyCheckSignature() !== this.lastSupplierCurrencyCheckSignature) {
            await this.refreshSelectableCurrencies();
        }
        if (this.companyCodeResetInProgress) {
            return;
        }
        // Taken right before the request: the last request started reads the newest server state, so a
        // slower, earlier one must not overwrite its list.
        const rebuildToken = ++this.articleRebuildToken;
        let articles: DemandArticle[];
        try {
            articles = await this.demand.getProductsAsArticles(this.itemAccountingDataService);
        } catch {
            if (rebuildToken === this.articleRebuildToken) {
                this.notificationService.showNotification(
                    marker('Die Positionen konnten nicht aktualisiert werden. Bitte laden Sie die Seite neu.'),
                    NotificationMessageLevel.WARNING,
                );
            }
            return;
        }
        if (rebuildToken !== this.articleRebuildToken) {
            return;
        }
        this.articles = articles;
        this.clearRecommendationState();
        this.cdr.markForCheck();
    }

    // See the EPROC-11619 comment at the ngOnInit call site for why this guard exists.
    private demandSetupStarted = false;

    private setDemand(): void {
        this.demandSetupStarted = true;
        if (this.isPurchaseRequisition) {
            // EPROC-11619: deliberately always reassigns `this.demand` and re-runs init(), even though
            // a task-hosting parent can reassign `task`/`purchaseRequisition` to a new object instance
            // for the very same demand more than once (e.g. a cached shell first, the fully loaded
            // task moments later, or after "Aufgabe mir zuweisen" flips demand.readOnly). Skipping the
            // reassignment here to dodge that used to leave `this.demand` stuck on stale data (readOnly
            // never updating after claiming the task). init() itself is what avoids losing already
            // collected per-item validity reports now (see DemandValidator.createPreservingItemReports).
            this.demand = this.purchaseRequisition!.demand;
            this.init();
        } else if (!this.demand) {
            this.route.params.pipe(takeUntil(this.onDestroy$)).subscribe(async (params: Params) => {
                const demandId: string = params['demandId'];
                if (typeof demandId !== 'undefined') {
                    // EPROC-11534: a stale link or deleted demand yields a 404 from Demand.byId; without
                    // the catch the rejection escapes the async subscribe callback unhandled (Sentry noise,
                    // page stuck on the loading spinner) instead of informing the user. Every emission
                    // starts a fresh attempt, so a previous failure must not stick to the next demand id.
                    this.error.set(undefined);
                    try {
                        await this.getDemand(demandId);
                    } catch (error: unknown) {
                        this.error.set(this.resolveDemandLoadErrorCode(error));
                        // markForCheck is enough here (default change detection, the NgZone tick renders)
                        // and stays harmless if the view was destroyed while the request was in flight.
                        this.cdr.markForCheck();
                        return;
                    }
                    await this.initLoadedDemand();
                }
            });
        } else {
            this.init();
        }
    }

    /**
     * EPROC-11534: init() is not local setup — it loads accountings and classifications per item
     * (Demand.getProductsAsArticles -> DemandArticle.from). Those failures must not be reported as
     * "demand not found / no permission" (the demand itself loaded fine) and must not hide it behind
     * an error page. The demand stays visible, the user learns that parts are missing, and a
     * non-HTTP failure is reported explicitly — otherwise it would vanish silently now that the
     * rejection no longer escapes to the global ErrorHandler (HTTP failures are already covered by
     * the ErrorInterceptor).
     */
    private async initLoadedDemand(): Promise<void> {
        try {
            await this.init();
        } catch (error: unknown) {
            if (!(error instanceof HttpErrorResponse)) {
                this.sentry.captureException(error);
            }
            this.notificationService.showNotification(DEMAND_DETAILS_LOAD_FAILED, NotificationMessageLevel.ERROR);
            this.cdr.markForCheck();
        }
    }

    private emitValidationChange(): void {
        const validForNextStep = this.validator?.validForNextStep() ?? false;
        if (this.validator && validForNextStep !== this.demandValid) {
            this.demandValid = validForNextStep;
            this.validationStatusChange.emit(validForNextStep);
        }
    }

    private async getDemand(demandId: string): Promise<void> {
        const newDemand = await Demand.byId(
            demandId,
            this.dataService,
            this.commentDataService,
            this.itemClassificationDataService,
            this.fileDataService,
            this.i18n,
        );
        this.breadcrumbService.set(BC.PURCHASE_REQUISITION.LIST, BC.PURCHASE_REQUISITION.DEMAND_CREATE);
        if (this.demand) {
            newDemand.readOnly = this.demand.readOnly;
        }
        this.currency = new DisplayCurrency(newDemand.currency, this.getUserLanguage());
        if (!this.demand) {
            this.demand = newDemand;
        } else {
            await this.demand.refresh();
        }
    }
}

function buildPurchasingGroupLabel(code?: string | null, name?: string | null): string | null {
    const normalizedCode = code?.trim();
    const normalizedName = name?.trim();

    if (normalizedCode && normalizedName) {
        return `${normalizedCode} - ${normalizedName}`;
    }

    return normalizedCode ?? normalizedName ?? null;
}

function createPurchasingGroupFallbackOption(code: string, name: string): IPurchasingGroupOption {
    const trimmedCode = code.trim();
    const trimmedName = (name ?? '').trim();
    const label = buildPurchasingGroupLabel(trimmedCode, trimmedName) ?? trimmedCode;

    return {
        code: trimmedCode,
        name: trimmedName,
        label,
    };
}

function toPurchasingGroupOption(purchasingGroup: PurchasingGroup): IPurchasingGroupOption {
    const code = purchasingGroup.code?.trim() ?? '';
    const name = purchasingGroup.name?.trim() ?? '';
    const label = buildPurchasingGroupLabel(code, name) ?? code ?? name ?? '';

    return {
        code,
        name,
        label,
    };
}
